/**
 * @copyright WaterStreet. All rights reserved.
 */

import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	CountryCodeNames
} from '@shared/constants/enums/country-code-names.enum';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	DateHelper
} from '@shared/helpers/date.helper';
import {
	InterpolationHelper
} from '@shared/helpers/interpolation.helper';
import {
	camelCase
} from 'lodash-es';
import {
	DateTime
} from 'luxon';
import pluralize from 'pluralize';
import {
	Md5
} from 'ts-md5';

/**
 * A class containing static helper methods
 * for the string type.
 *
 * @export
 * @class StringHelper
 */
export class StringHelper
{
	/**
	 * Gets the standard international format to use in this helper.
	 *
	 * @static
	 * @type {string}
	 * @memberof StringHelper
	 */
	private static readonly internationalFormat: string = 'en-US';

	/**
	 * Gets a collection of default whitespace characters.
	 *
	 * @static
	 * @type {string[]}
	 * @memberof StringHelper
	 */
	private static readonly defaultWhitespace: string[]
		= [
			String.fromCodePoint(9), // Tab
			String.fromCodePoint(10), // LF
			String.fromCodePoint(11), // VT
			String.fromCodePoint(12), // FF
			String.fromCodePoint(13), // CR
			String.fromCodePoint(32) // Space
		];

	/**
	 * List of country codes whose postal codes
	 * are placed before the city in a full address format.
	 *
	 * @static
	 * @type {string[]}
	 * @memberof StringHelper
	 */
	private static readonly offPostalCountryCodes: string[]
		= [
			'ALA',
			'AUT',
			'BEL',
			'CHE',
			'DEU',
			'DNK',
			'FIN',
			'FRA',
			'FRO',
			'ISL',
			'ISR',
			'LIE',
			'LUX',
			'NLD',
			'NOR',
			'SVK',
			'SVN',
			'SWE'
		];

	/**
	 * The USA Country Code.
	 *
	 * @static
	 * @type {string}
	 * @memberof StringHelper
	 */
	private static readonly uSACountryCode: string = 'USA';

	/**
	 * Returns the string in proper case format.
	 *
	 * @static
	 * @param {string} value
	 * The string to set to a proper case value.
	 * @returns {string}
	 * The proper case string representation of the parameter
	 * value.
	 * @memberof StringHelper
	 */
	public static toProperCase(
		value: string): string
	{
		return (value == null
			|| value.length === 0)
			? value
			: value
				.charAt(0)
				.toUpperCase()
					+ value.slice(1);
	}

	/**
	 * Returns the string with added spaces before each capital letter.
	 *
	 * @static
	 * @param {string} value
	 * The string value with capital letters.
	 * @returns {string}
	 * The string value with added spaces before each capital letter.
	 * @memberof StringHelper
	 */
	public static beforeCapitalSpaces(
		value: string): string
	{
		return (value == null
			|| value.length === 0)
			? value
			: value
				.replace(
					/([a-z])([A-Z])/g,
					'$1 $2');
	}

	/**
	 * Returns the string with added spaces before each capital letter.
	 *
	 * @static
	 * @param {string} value
	 * The string value with numbers.
	 * @returns {string}
	 * The string value with added spaces between each number.
	 * @memberof StringHelper
	 */
	public static betweenNumberSpaces(
		value: string): string
	{
		return (value == null
			|| value.length === 0)
			? value
			: value
				.replace(
					/(?:[a-z])(?=\d)|(?:\d)(?=[a-z])/gi,
					'$& ');
	}

	/**
	 * Gets a left and right trimmed version of the input $this value.
	 * If no args are specified, the default whitespace characters are
	 * used to trim the $this.
	 *
	 * @static
	 * @param {string} $this A string to be trimmed.
	 * @param {...string[]} args A collection of characters to trim.
	 * @returns {string} A trimmed representation of $this.
	 * @memberof StringHelper
	 */
	public static trim(
		$this: string,
		...args: string[]): string
	{
		return this.trimRight(
			this.trimLeft(
				$this,
				...args),
			...args);
	}

	/**
	 * Gets a left trimmed version of the input $this value.
	 * If no args are specified, the default whitespace characters are
	 * used to trim the $this.
	 *
	 * @static
	 * @param {string} $this A string to be trimmed.
	 * @param {...string[]} args A collection of characters to trim.
	 * @returns {string} A trimmed representation of $this.
	 * @memberof StringHelper
	 */
	public static trimLeft(
		$this: string,
		...args: string[]): string
	{
		if (AnyHelper.isNull($this))
		{
			return null;
		}

		if (args.length === 0)
		{
			args.push(...this.defaultWhitespace);
		}

		while (args.includes($this.charAt(0)))
		{
			// eslint-disable-next-line no-param-reassign
			$this = $this.substring(1);
		}

		return $this;
	}

	/**
	 * Gets a right trimmed version of the input $this value.
	 * If no args are specified, the default whitespace characters are
	 * used to trim the $this.
	 *
	 * @static
	 * @param {string} $this A string to be trimmed.
	 * @param {...string[]} args A collection of characters to trim.
	 * @returns {string} A trimmed representation of $this.
	 * @memberof StringHelper
	 */
	public static trimRight(
		$this: string,
		...args: string[]): string
	{
		if (AnyHelper.isNull($this))
		{
			return null;
		}

		if (args.length === 0)
		{
			args.push(...this.defaultWhitespace);
		}

		while (args.includes($this.charAt($this.length - 1)))
		{
			// eslint-disable-next-line no-param-reassign
			$this = $this.substring(0, $this.length - 1);
		}

		return $this;
	}

	/**
	 * Gets an MD5 hash representing the inputed $this.
	 *
	 * @static
	 * @param {string} $this A string to get MD5 hash for.
	 * @returns {string} A string representing the MD5 hash for $this.
	 * @memberof StringHelper
	 */
	public static getHash(
		$this: string): string
	{
		if (AnyHelper.isNullOrEmpty($this))
		{
			return null;
		}

		return Md5.hashStr($this);
	}

	/**
	 * Returns a cleaned string, this will accept an array of strings to be
	 * removed and return the original with these no longer in the value.
	 *
	 * @static
	 * @param {string} value
	 * The string to set to clean.
	 * @param {string[]} [stringsToRemove]
	 * The array of strings to be removed.
	 * The default value removes the space character.
	 * @returns {string}
	 * The string with all sent parameter strings removed.
	 * @memberof StringHelper
	 */
	public static getCleanedValue(
		value: string,
		stringsToRemove: string[] = [' ']): string
	{
		if (AnyHelper.isNullOrEmpty(value))
		{
			return value;
		}

		let formattedValue: string = value;
		let regexReplace: RegExp;
		stringsToRemove.forEach((string: string) =>
		{
			regexReplace = new RegExp(string, 'g');
			formattedValue =
				formattedValue.replace(regexReplace, '');
		});

		return formattedValue;
	}

	/**
	 * Returns the final string value with trimmed whitespaces,
	 * following the splitting of the string on the sent split
	 * character.
	 *
	 * @static
	 * @param {string} value
	 * The string to split on the sent split character. Thi
	 * @param {string} [splitCharacter]
	 * The character to split this string on. If this character is not found,
	 * the full trimmed string is returned. This defaults to the ',' character.
	 * @returns {string}
	 * The final string value following the split character sent.
	 * @memberof StringHelper
	 */
	public static getLastSplitValue(
		value: string,
		splitCharacter: string = AppConstants.characters.comma): string
	{
		if (AnyHelper.isNullOrEmpty(value))
		{
			return value;
		}

		const splitStrings: string[] =
			value.split(splitCharacter);

		return StringHelper.trim(
			splitStrings[splitStrings.length - 1]);
	}

	/**
	 * Returns a string title representation of the entity type.
	 *
	 * @static
	 * @param {string} entityType
	 * the entity type.
	 * @param {string} [subType]
	 * THe optional sub type added after a hyphen.
	 * @param {bool} [excludeBaseType]
	 * The optional value indicating if the first or base
	 * entity type should be included.
	 * @returns {string}
	 * The result title.
	 * @memberof StringHelper
	 */
	public static getEntityTypeTitle(
		entityType: string,
		subType: string = null,
		excludeBaseType = true): string
	{
		const split = entityType.split(AppConstants.characters.period);

		if (split.length <= 1)
		{
			return entityType;
		}

		if (excludeBaseType)
		{
			split.shift();
		}

		const title: string =  this.beforeCapitalSpaces(
			split.join(AppConstants.empty));

		return AnyHelper.isNullOrEmpty(subType)
			? title
			: title + ` - ${subType}`;
	}

	/**
	 * Returns a concatenated string value.
	 *
	 * @static
	 * @param {string} value
	 * The string value to add to the concatenate string.
	 * @param {string} concatenateString
	 * The concatenated string.
	 * @param {string} concatenateKey
	 * The concatenate key.
	 * @returns {string}
	 * The concatenated string.
	 * @memberof StringHelper
	 */
	 public static getConcatenatedValue(
		value: string,
		concatenateString: string,
		concatenateKey: string = null): string
	{
		if (AnyHelper.isNullOrEmpty(value))
		{
			return concatenateString;
		}

		return concatenateString.concat(
			AnyHelper.isNull(concatenateKey)
				? value
				: concatenateKey, value);
	}

	/**
	 * Returns a parsed camelcase string by separating each capitalized
	 * word as space separated. This will return all values capitalized
	 * unless the overload {useProperCase} is set to false.
	 *
	 * @static
	 * @param {string} value
	 * The camelcase string to normalize by adding a space prior to
	 * capitalizations.
	 * @param {boolean} [useProperCase]
	 * A truthy defining if each camel case 'word' is capitalized.
	 * @returns {string}
	 * The normalized with spaces camelcase string.
	 * @memberof StringHelper
	 */
	public static normalizeCamelcase(
		value: string,
		useProperCase: boolean = true): string
	{
		if (AnyHelper.isNullOrEmpty(value))
		{
			return value;
		}

		const formattedValue: string =
			StringHelper.trim(
				value.replace(/([A-Z])/g, ' $1'));

		return useProperCase
			? formattedValue.replace(/^./,
				function(substring: string)
				{
					return substring.toUpperCase();
				})
			: formattedValue.toLowerCase();
	}

	// Original algorithm
	// eslint-disable-next-line max-len
	// https://ourcodeworld.com/articles/read/353/how-to-convert-numbers-to-words-number-spelling-in-javascript
	/**
	 * Accepts whole number inputs and returns the english representation of the
	 * number sent.
	 *
	 * @static
	 * @param {string} value
	 * The string that will have digits translated into words.
	 * @param {boolean} [useProperCase]
	 * Whether or not number values should be capitalized in the output.
	 * The default value is to not capitalize number values.
	 * @returns {string}
	 * An english number to word translation for the sent value.
	 * @memberof StringHelper
	 */
	public static numberToWords(
		value: string,
		useProperCase: boolean = false): string
	{
		/* eslint-disable @typescript-eslint/no-explicit-any */
		const string = value.toString();
		const and = 'and';

		let end: number;
		let chunk: number;
		let ints: number[];
		let index: number;
		let word: string;

		if (!/^\d+$/.test(string))
		{
			return 'NaN';
		}

		// Is number zero?
		if (parseInt(string, 10) === 0) {
			return useProperCase
				? 'Zero'
				: 'zero';
		}

		// Array of units as words.
		const units: string[] =
		[
			'',
			'one',
			'two',
			'three',
			'four',
			'five',
			'six',
			'seven',
			'eight',
			'nine',
			'ten',
			'eleven',
			'twelve',
			'thirteen',
			'fourteen',
			'fifteen',
			'sixteen',
			'seventeen',
			'eighteen',
			'nineteen'
		];

		// Array of tens as words.
		const tens: string[] =
		[
			'',
			'',
			'twenty',
			'thirty',
			'forty',
			'fifty',
			'sixty',
			'seventy',
			'eighty',
			'ninety'
		];

		// Array of scales as words.
		const scales: string[] =
			[
				'',
				'thousand',
				'million',
				'billion',
				'trillion',
				'quadrillion',
				'quintillion',
				'sextillion',
				'septillion',
				'octillion',
				'nonillion',
				'decillion',
				'undecillion',
				'duodecillion',
				'tredecillion',
				'quatttuor-decillion',
				'quindecillion',
				'sexdecillion',
				'septen-decillion',
				'octodecillion',
				'novemdecillion',
				'vigintillion',
				'centillion'
			];

		// Split user argument into 3 digit chunks from right to left.
		let start: number = string.length;
		const chunks: any[] | string[] = [];

		while (start > 0)
		{
			end = start;
			start = Math.max(0, start - 3);
			chunks.push(
				string.slice(
					start,
					end));
		}

		// Check if function has enough scale words
		// to be able to stringify the user argument.
		const chunksLen: number = chunks.length;
		if (chunksLen > scales.length)
		{
			return 'Overflow';
		}

		// Stringify each integer in each chunk.
		const words: any[] = [];
		for (index = 0; index < chunksLen; index++)
		{
			chunk = parseInt(chunks[index], 10);

			if (!AnyHelper.isNullOrEmpty(chunk))
			{
				// Split chunk into array of individual integers.
				const intChunks =
					chunks[index]
						.split('')
						.reverse();
				ints = intChunks
					.map(parseFloat);

				// If tens integer is 1, i.e. 10, then add 10 to units integer.
				if (ints[1] === 1)
				{
					ints[0] += 10;
				}

				// Add scale word if chunk is not zero and array item exists.
				word = scales[index];
				if (!AnyHelper.isNullOrEmpty(word))
				{
					words.push(
						useProperCase
							? this.toProperCase(word)
							: word);
				}

				// Add unit word if array item exists.
				word = units[ints[0]];
				if (!AnyHelper.isNullOrEmpty(word))
				{
					words.push(
						useProperCase
							? this.toProperCase(word)
							: word);
				}

				// Add tens word if array item exists.
				word = tens[ints[1]];
				if (!AnyHelper.isNullOrEmpty(word))
				{
					words.push(
						useProperCase
							? this.toProperCase(word)
							: word);
				}

				// Add 'and' string after units or tens integer if:
				if (ints[0] || ints[1])
				{

					// Chunk has a hundreds integer or chunk is the first
					// of multiple chunks.
					// Altered check for chunksLen > 1 to remove 'and'
					// on single digits.
					if (ints[2] || !index && chunksLen > 1) {
						words.push(and);
					}

				}

				// Add hundreds word if array item exists.
				word = units[ints[2]];
				if (!AnyHelper.isNullOrEmpty(word))
				{
					words.push(
						useProperCase
							? this.toProperCase(word) + ' Hundred'
							: word + ' hundred');
				}
			}
		}

		words.reverse();

		return words.join(AppConstants.characters.space);
	}

	/**
	 * Maps the provided data to the provided template
	 * using string interpolation syntax.
	 *
	 * @static
	 * @param template
	 * the template.
	 * @param data
	 * thet data to map to the template.
	 * @returns {string}
	 * @memberof StringHelper
	 */
	public static interpolate(
		template: string,
		data: any): string
	{
		return InterpolationHelper
			.getTemplateParser(template)(data);
	}

	/**
	 * Returns a parsed string as a context level function.
	 * Sample usage:
	 * StringHelper.transformToFunction(
	 * 	'return function() { return { chartColors: [ \"primary\" ] }; }',
	 * 	this);
	 *
	 * @static
	 * @param {string} functionAsString
	 * The function as a string.
	 * @param {any} context
	 * The data context or closure this function will run against.
	 * @memberof StringHelper
	 */
	public static transformToFunction(
		functionAsString: string,
		context: any): Function
	{
		return Function(functionAsString)()
			.bind(context);
	}

	/**
	 * Returns a boolean value from a parsed string as a context level data
	 * promise.
	 *
	 * @static
	 * @async
	 * @param {string} booleanPromiseAsString
	 * The function as a string.
	 * @param {any} pageContext
	 * The data context or closure this function will run against.
	 * @returns {Promise<boolean>}
	 * The value found via executing this function against the page context.
	 * @memberof StringHelper
	 */
	public static async transformAndExecuteBooleanPromise(
		booleanPromiseAsString: string,
		pageContext: any): Promise<boolean>
	{
		if (AnyHelper.isNull(pageContext))
		{
			return true;
		}

		const booleanPromise: Promise<boolean> =
			StringHelper.transformToDataPromise(
				booleanPromiseAsString,
				pageContext);

		return booleanPromise;
	}

	/**
	 * Returns a parsed string as a context level data promise.
	 * Sample usage:
	 * StringHelper.transformToDataPromise(
	 * 	'return function() { return this.apiService.apiMethod(); }',
	 * 	this);
	 *
	 * @static
	 * @param {string} dataPromise
	 * The function as a string.
	 * @param {any} context
	 * The data context or closure this function will run against.
	 * @memberof StringHelper
	 */
	public static transformToDataPromise(
		dataPromise: string,
		context: any): Promise<any>
	{
		return new Promise(
			function(resolve: any)
			{
				resolve(
					Function(dataPromise)()
						.bind(context)());
			}.bind(context));
	}

	/**
	 * Returns a parsed string as a field and context level data promise.
	 * Sample usage:
	 * StringHelper.transformToFieldDataPromise(
	 * 	'return function() { return if (field.formControl.value == true)
	 * 		{this.apiService.apiMethod();} }',
	 * 	this);
	 *
	 * @static
	 * @param {string} dataPromise
	 * The function as a string.
	 * @param {any} context
	 * The data context or closure this function will run against.
	 * @param {any} field
	 * The field context this function will run against.
	 * @memberof StringHelper
	 */
	public static transformToFieldDataPromise(
		dataPromise: string,
		context: any,
		field: any): Promise<any>
	{
		return new Promise(
			function(resolve: any)
			{
				resolve(
					Function(dataPromise)()
						.bind(context, field)());
			}.bind(context));
	}

	/**
	 * Returns a parsed string as a parameter and context level data promise.
	 * Sample usage:
	 * StringHelper.transformToValueDataPromise(
	 * 	'return function(value) { return if (value == true)
	 * 		{this.apiService.apiMethod();} }',
	 * 	this);
	 *
	 * @static
	 * @param {string} dataPromise
	 * The function as a string.
	 * @param {any} context
	 * The data context or closure this function will run against.
	 * @param {any} field
	 * The field context this function will run against.
	 * @memberof StringHelper
	 */
	public static transformToValueDataPromise(
		dataPromise: string,
		context: any,
		parameter: any): Promise<any>
	{
		return this.transformToFieldDataPromise(
			dataPromise,
			context,
			parameter);
	}

	/**
	 * Returns a parsed string as a context level layout promise.
	 * Sample usage:
	 * StringHelper.transformToLayoutPromise(
	 * 	'return function(field, event) { return console.log(this.field); }',
	 * 	this.field,
	 * 	event,
	 * 	this.field.props.context);
	 *
	 * @static
	 * @param {string} promiseString
	 * The function as a string.
	 * @param {any} field
	 * The formly layout field.
	 * @param {any} event
	 * The event being triggered.
	 * @param {any} context
	 * The data context or closure this function will run against.
	 * @memberof StringHelper
	 */
	public static transformToLayoutPromise(
		promiseString: string,
		field: any,
		event: any,
		context: any): Promise<any>
	{
		return new Promise(
			function(resolve: any)
			{
				resolve(
					(Function(promiseString)()
						.bind(context,
							field,
							event))());

			}.bind(context));
	}

	/**
	 * Creates a text area element that accepts new lines
	 * and sets the value as the sent text. This value is
	 * then copied as formatted text into the clipboard.
	 *
	 * @async
	 * @param {string} text
	 * The text value to copy to the clipboard.
	 * @memberof StringHelper
	 */
	public static async copyToClipboard(
		text: string): Promise<void>
	{
		await navigator.clipboard.writeText(text);
	}

	/**
	 * Creates a comma separated string representation of the sent
	 * array string values.
	 *
	 * @param {enumeration: string[]} enumeration
	 * The array of values to translate into a sentence structured list
	 * of values.
	 * @param {enumeration: string[]} useAnd
	 * If sent and true, this will use the and value for lists larger than two
	 * in size otherwise the last item will be appended with or. This value
	 * defaults to true.
	 * @memberof StringHelper
	 */
	public static getProperEnumeration(
		enumeration: string[],
		useAnd: boolean = true): string
	{
		if (AnyHelper.isNull(enumeration))
		{
			return AppConstants.empty;
		}

		let filter: string = AppConstants.empty;
		const commaOrEmpty: string =
			enumeration.length === 2
				? AppConstants.empty
				: AppConstants.characters.comma;
		enumeration.forEach((enumerable: string, index: number) =>
		{
			const predicate: string =
				useAnd === true
					? 'and'
					: 'or';
			const existingFilterCriteria =
				index === enumeration.length - 1
					? `${commaOrEmpty} `
						+ `${predicate} ${enumerable}`
					: `${commaOrEmpty} ${enumerable}`;

			filter +=
				AnyHelper.isNullOrWhitespace(filter)
					? `${enumerable}`
					: existingFilterCriteria;
		});

		return filter;
	}

	/**
	 * Creates a returns a string value used to interpolate properties
	 * in a nested data object.
	 *
	 * @param {string} value
	 * The string to gather the data key value for.
	 * @returns {string}
	 * A a key value for use in an object containing nested data.
	 * @memberof StringHelper
	 */
	public static clearNestedDataIdentifier(
		value: string): string
	{
		return value?.replace(
			AppConstants.nestedDataIdentifier,
			AppConstants.empty);
	}

	/**
	 * Formats a number to a currency value.
	 *
	 * @param {number} value
	 * The number to be formatted to currency.
	 * @param {currency} value
	 * The currency option. USD by default.
	 * @param {locales} value
	 * The locale currency format. en-US by default.
	 * @returns {string}
	 * A currency representation of the provided number.
	 * @memberof StringHelper
	 */
	public static numberToCurrency(
		value: number,
		currency: string = 'USD',
		locales: string = this.internationalFormat): string
	{
		const currencyOptions: any =
			<any>
			{
				style: 'currency',
				currency: currency
			};

		// Add whole number rounding if applicable.
		if (value % 1 === 0)
		{
			currencyOptions.maximumSignificantDigits = 10;
		}

		const currencyFormat =
			new Intl.NumberFormat(
				locales,
				currencyOptions);

		return currencyFormat.format(
			parseFloat(value.toFixed(2)));
	}

	/**
	 * Capitalizes every initial letter on a string value that
	 * is separated by spaces.
	 *
	 * @param {string} value
	 * The string value to be formatted.
	 * @returns {string}
	 * A string value capitalized on every initial letter.
	 * @memberof StringHelper
	 */
	public static capitalizeAllWords(value: string): string
	{
		const splitValues: string[] =
			value.split(AppConstants.characters.space);
		const upperCaseArray: string[] = [];
		for (const splitValue of splitValues)
		{
			upperCaseArray.push(StringHelper.toProperCase(splitValue));
		}

		return upperCaseArray.join(AppConstants.characters.space);
	}

	/**
	 * Shortens a string value to the sent length.
	 *
	 * @param {string} value
	 * The string value to be shortened.
	 * @param {number} length
	 * The length to shorten the string value to.
	 * @returns {string}
	 * A shortened string value.
	 * @memberof StringHelper
	 */
	public static shorten(
		value: string,
		length: number): string
	{
		if ((value?.length ?? 0) <= length)
		{
			return value;
		}

		return value.substring(
			0,
			length)
			+ '...';
	}

	/**
	 * Returns a string value representing all separated by period types
	 * proper-cased and with spaces replacing each period.
	 * @remarks Example: 'this.is.a.testItem' -> 'This Is A Test Item'
	 * @remarks See also: StringHelper.format with the type of
	 * entityTypeDisplay for the short display name of an entity type.
	 *
	 * @param {string} value
	 * The string value to be formatted.
	 * @param {string} replaceValue
	 * The value to be replaced with an empty string. This is used to remove
	 * prefixes such as 'Organization.'.
	 * @returns {string}
	 * A string value used to get the long display name of an entity.
	 * @memberof StringHelper
	 */
	public static getLongEntityDisplayName(
		value: string,
		replaceValue: string = null): string
	{
		let formattedValue: string =
			!AnyHelper.isNullOrEmpty(replaceValue)
				? value.replace(
					replaceValue,
					AppConstants.empty)
				: value;

		formattedValue =
			StringHelper.toProperCase(
				StringHelper.beforeCapitalSpaces(
					formattedValue));

		return formattedValue
			.replace(
				new RegExp(
					`\\${AppConstants.characters.period}`,
					AppConstants.regularExpressionFlags.global),
				AppConstants.characters.space);
	}

	/**
	 * Formats the string into an specific format type.
	 *
	 * @static
	 * @param {string} value
	 * The string value to be formatted.
	 * @param {string} formatType
	 * The format type to format the string.
	 * Available format types in AppConstants.formatTypes.
	 * @returns {string}
	 * The formatted string.
	 * @memberof StringHelper
	 */
	public static format(
		value: string,
		formatType: string): string
	{
		if (AnyHelper.isNull(value)
			|| formatType === AppConstants.formatTypes.none)
		{
			return value;
		}

		let formattedValue: string;
		const parsedInt: number =
			parseInt(
				value,
				AppConstants.parseRadix);
		const parsedFloat: number =
			parseFloat(
				value);

		switch (formatType)
		{
			case AppConstants.formatTypes.boolean:
			case AppConstants.formatTypes.yesNoBoolean:
				formattedValue =
					StringHelper.formatBooleanValue(
						value,
						formatType === AppConstants.formatTypes.yesNoBoolean);
				break;
			case AppConstants.formatTypes.currency:
				formattedValue =
					this.numberToCurrency(
						parsedFloat);
				break;
			case AppConstants.formatTypes.longDate:
			case AppConstants.formatTypes.shortDate:
				formattedValue =
					DateHelper.formatDate(
						DateTime.fromISO(value),
						formatType === AppConstants.formatTypes.longDate
							? DateHelper.presetFormats.longDateFormat
							: DateHelper.presetFormats.shortDateFormat);
				break;
			case AppConstants.formatTypes.percent:
				formattedValue =
					Intl.NumberFormat(
						this.internationalFormat,
						{
							style: 'percent',
							minimumFractionDigits: 0
						})
						.format(parsedFloat);
				break;
			case AppConstants.formatTypes.number:
				formattedValue =
					Intl.NumberFormat(
						this.internationalFormat)
						.format(parsedInt);
				break;
			case AppConstants.formatTypes.beforeCapitalSpaces:
				formattedValue =
					StringHelper.toProperCase(
						StringHelper.beforeCapitalSpaces(
							value));
				break;
			case AppConstants.formatTypes.entityTypeDisplay:
				formattedValue =
					StringHelper.beforeCapitalSpaces(
						StringHelper.getLastSplitValue(
							value,
							AppConstants.characters.period));
				break;
			default:
				formattedValue = value;
				break;
		}

		return formattedValue;
	}

	/**
	 * Formats a string representing a boolean value into a formatted value
	 * of True/False or Yes/No depending on the yesNoOutput paramater.
	 *
	 * @static
	 * @param {string | boolean} value
	 * The string value to be checked for true equivalence.
	 * @param {boolean} yesNoOutput
	 * If sent this will format true as Yes and false as No. This value
	 * defaults to false.
	 * @returns {string}
	 * The formatted boolean based string.
	 * @memberof StringHelper
	 */
	public static formatBooleanValue(
		value: string | boolean,
		yesNoOutput: boolean = false): string
	{
		let nullHandler: string;
		let formattedValue: string;
		const booleanValue: boolean =
			value === 'true' || value === true;

		if (yesNoOutput === false)
		{
			nullHandler =
				AnyHelper.isNullOrWhitespace(value)
					? AppConstants.empty
					: 'False';

			formattedValue =
				booleanValue === true
					? 'True'
					: nullHandler;
		}
		else
		{
			nullHandler =
				AnyHelper.isNullOrWhitespace(value)
					? AppConstants.empty
					: 'No';

			formattedValue =
				booleanValue === true
					? 'Yes'
					: nullHandler;
		}

		return formattedValue;
	}

	/**
	 * Returns a name value that will combine the first and last name if both
	 * exist, return one or the other if only one exists, or returns
	 * an empty string if neither value exists.
	 *
	 * @static
	 * @param {string} firstName
	 * The first name string.
	 * @param {string} lastName
	 * The last name string.
	  * @param {string} legalName
	 * The legal name string.
	 * @returns {string}
	 * A formatted name based string.
	 * @memberof StringHelper
	 */
	public static toNameString(
		firstName: string,
		lastName: string,
		legalName: string = null): string
	{
		const firstNameExists: boolean =
			!AnyHelper.isNullOrWhitespace(firstName);
		const lastNameExists: boolean =
			!AnyHelper.isNullOrWhitespace(lastName);
		const legalNameExists: boolean =
			!AnyHelper.isNullOrWhitespace(legalName);

		if (legalNameExists === false)
		{
			if (firstNameExists === false && lastNameExists === false)
			{
				return AppConstants.empty;
			}

			if (firstNameExists === true && lastNameExists === true)
			{
				return `${firstName} ${lastName}`;
			}

			return firstNameExists
				? firstName
				: lastName;
		}

		if (firstNameExists === false && lastNameExists === false)
		{
			return legalName;
		}

		if (firstNameExists === true && lastNameExists === true)
		{
			return `${legalName}, ${firstName} ${lastName}`;
		}

		return firstNameExists
			? `${legalName}, ${firstName}`
			: `${legalName}, ${lastName}`;
	}

	/**
	 * Returns a name value by checking display name,
	 * legal name, and first/last names in order. Returns
	 * '[Deleted]' if none of the above exist.
	 *
	 * @static
	 * @param {any} nameObject
	 * The interest name object.
	 * @param {any} namePrefix
	 * The prefix added to the returned name value.
	 * @returns {string}
	 * A formatted name based string.
	 * @memberof StringHelper
	 */
	public static fromNameObject(
		nameObject: any,
		namePrefix: string = null): string
	{
		const displayNameExists: boolean =
			!AnyHelper.isNullOrWhitespace(nameObject?.displayName);
		const firstNameExists: boolean =
			!AnyHelper.isNullOrWhitespace(nameObject?.firstName);
		const lastNameExists: boolean =
			!AnyHelper.isNullOrWhitespace(nameObject?.lastName);
		const legalNameExists: boolean =
			!AnyHelper.isNullOrWhitespace(nameObject?.legalName);
		const interestTypeExists: boolean =
			!AnyHelper.isNullOrWhitespace(namePrefix);
		const interestTypePrefix =
			interestTypeExists === false
				? AppConstants.empty
				: this.format(
					namePrefix,
					AppConstants.formatTypes.beforeCapitalSpaces)
					.replace('Co ', 'Co-');

		if (displayNameExists === true)
		{
			return interestTypeExists
				? `${interestTypePrefix} - ${nameObject.displayName}`
				: `${nameObject.displayName}`;
		}

		if (legalNameExists === true)
		{
			return interestTypeExists
				? `${interestTypePrefix} - ${nameObject.legalName}`
				: `${nameObject.legalName}`;
		}

		if (firstNameExists || lastNameExists)
		{
			const combinedName: string =
				[nameObject?.firstName, nameObject?.lastName]
					.filter(name =>
						!AnyHelper.isNullOrWhitespace(name))
					.join(
						AppConstants.characters.space);

			return interestTypeExists
				? `${interestTypePrefix} - ${combinedName}`
				: combinedName;
		}

		return '[Deleted]';
	}

	/**
	 * Returns an address value that will return the full set of existing data
	 * without extra spaces.
	 *
	 * @static
	 * @param {string} address
	 * The address string.
	 * @param {string} city
	 * The city string.
	 * @param {string} state
	 * The state string.
	 * @param {string} principalSubdivision
	 * The principal subdivision string.
	 * @param {string} postalCode
	 * The postal code string.
	 * @param {string} countryCode
	 * The country string.
	 * @returns {string}
	 * A formatted address based string.
	 * @memberof StringHelper
	 */
	public static toAddressString(
		address: string,
		city: string,
		state: string,
		postalCode: string,
		principalSubdivision: string = null,
		countryCode: string = this.uSACountryCode): string
	{
		const commaPossibleValue: string =
			AnyHelper.isNullOrWhitespace(state) ||
				AnyHelper.isNullOrWhitespace(postalCode)
				? AppConstants.empty
				: ', ';

		const addressValue: string =
			AnyHelper.isNullOrWhitespace(address)
				? AppConstants.empty
				: `${address} `;
		const cityValue: string =
			AnyHelper.isNullOrWhitespace(city)
				? AppConstants.empty
				: `${city} `;
		const stateValue: string =
			(AnyHelper.isNullOrWhitespace(state)
				? AppConstants.empty
				: state)
				+ commaPossibleValue;
		const principalSubdivisionValue: string =
			(AnyHelper.isNullOrWhitespace(principalSubdivision)
				? AppConstants.empty
				: `${principalSubdivision} `);
		const postalCodeValue: string =
			AnyHelper.isNullOrWhitespace(postalCode)
				? AppConstants.empty
				: postalCode;
		const countryName: string =
			CountryCodeNames[countryCode] ?? null;

		const internationalAddress: string =
			this.offPostalCountryCodes
				.some((country: string) =>
					country === countryCode)
				? `${addressValue}${postalCodeValue} ${cityValue}`
					+ `${principalSubdivisionValue}${countryName}`
				: `${addressValue}${cityValue}${principalSubdivisionValue}`
					+ `${postalCodeValue} ${countryName}`;

		const uSPSStandardAddress: string =
			AnyHelper.isNullOrEmpty(countryName)
			|| countryCode === this.uSACountryCode
				? `${addressValue}${cityValue}${stateValue}`
					+ `${postalCodeValue}`
				: internationalAddress;

		return uSPSStandardAddress.toUpperCase();
	}

	/**
	 * Remove tabs and NewLines from the provided string.
	 *
	 * @static
	 * @param {string} value.
	 * @returns {string} A string without tabs and NewLines.
	 * @memberof StringHelper
	 */
	public static removeTabsAndNewLines(
		value: string): string
	{
		return value
			.replace(/(?:\n|\r|\t)/g, AppConstants.empty);
	}

	/**
	 * Converts a given string into a camel-case string.
	 *
	 * @static
	 * @param {string} value
	 * The input string to convert.
	 * @return {string}
	 * Returns a new camel case string representing the input value.
	 * @memberof StringHelper
	 */
	public static toCamelCase(
		value: string): string
	{
		return camelCase(value);
	}

	/**
	 * Checks whether the sent string is a valid GUID.
	 *
	 * @static
	 * @param {string} value
	 * The input string to check.
	 * @return {boolean}
	 * Returns whether or not the sent string is a GUID value.
	 * @memberof StringHelper
	 */
	public static isGuid(
		value: string): boolean
	{
		const partOne: string = '^[0-9a-fA-F]{8}-';
		const partTwo: string  = '[0-9a-fA-F]{4}-';
		const partThree: string  = '[1-5][0-9a-fA-F]{3}-';
		const partFour: string  = '[89abAB][0-9a-fA-F]{3}-';
		const partFive: string  = '[0-9a-fA-F]{12}$';

		const guidRegularExpression: RegExp =
			new RegExp(
				`${partOne}${partTwo}${partThree}${partFour}${partFive}`);

		return guidRegularExpression.test(value);
	}

	/**
	 * Checks if value is a valid JSON format.
	 *
	 * @param {string} value
	 * The string to check for a valid Json format.
	 * @returns {boolean}
	 * Returns whether or not the sent string is a valid JSON format.
	 * @memberof DisplayDefinitionsComponent
	 */
	public static isValidJson(
		value: string): boolean
	{
		let isValidJson: boolean = true;

		try
		{
			JSON.parse(value);
		}
		catch
		{
			isValidJson = false;
		}

		return isValidJson;
	}

	/**
	 * Splits numbers and words in a string by adding a space between them.
	 *
	 * @static
	 * @param {string} value
	 * The input string to split.
	 * @return {string}
	 * Returns the modified string with spaces between numbers and words.
	 * @memberof StringHelper
	 */
	public static splitNumbersAndWords(value: string): string
	{
		return value
			?.replace(
				/(\d)([a-zA-Z])/g,
				'$1 $2')
			?.replace(/([a-zA-Z])(\d)/g,
				'$1 $2')
			?? null;
	}

	/**
	 * Formats a string array into a sentence format. This will add commas
	 * and an 'and'/'or' separator for the last value.
	 *
	 * @static
	 * @param {string[]} array
	 * The string array to format.
	 * @param {string} separator
	 * The seperator to use for the last value. Defaults to 'and'.
	 * @return {string}
	 * Returns the formatted sentence output of the sent string array.
	 * @memberof StringHelper
	 */
	public static formatStringArrayToPartialSentence(
		array: string[],
		separator: string = 'and'): string
	{
		if (AnyHelper.isNullOrEmpty(array))
		{
			return AppConstants.empty;
		}

		const lastValue: string = array.pop();
		const partialSentence: string =
			array.join(
				`${AppConstants.characters.comma} `)
				+ (array.length > 1
					? AppConstants.characters.comma
					: AppConstants.empty)
				+ (array.length > 0
					? ` ${separator} `
					: AppConstants.empty)
				+ lastValue;

		return partialSentence;
	}

	/**
	 * Converts a plural word to its singular form using English grammar rules.
	 * Handles irregular plurals (people→person, children→child) and standard
	 * conversions (addresses→address, policies→policy, companies→company).
	 *
	 * @static
	 * @param {string} value
	 * The plural word to convert.
	 * @returns {string}
	 * The singular form of the word.
	 * @memberof StringHelper
	 */
	public static toSingular(value: string): string
	{
		if (AnyHelper.isNullOrWhitespace(value))
		{
			return AppConstants.empty;
		}

		return pluralize.singular(value);
	}

	/**
	 * Converts a singular word to its plural form using English grammar rules.
	 * Handles irregular plurals (person→people, child→children) and standard
	 * conversions (address→addresses, policy→policies, company→companies).
	 *
	 * @static
	 * @param {string} value
	 * The singular word to convert.
	 * @returns {string}
	 * The plural form of the word.
	 * @memberof StringHelper
	 */
	public static toPlural(value: string): string
	{
		if (AnyHelper.isNullOrWhitespace(value))
		{
			return AppConstants.empty;
		}

		return pluralize.plural(value);
	}
}